import classNames from 'classnames'
import { createRef, Fragment, useEffect, useRef, useState } from 'react'
import BSCard from 'react-bootstrap/Card'
import ReactHtmlParser from 'react-html-parser'
import { v4 } from 'uuid'

import Container from '@/components/Container/Container'
import { ExplicitHead } from '@/components/DefaultHead/DefaultHead'
import Main from '@/components/Main/Main'
import { PostType } from '@/types/wordpress'
import { formatUrl } from '@/utils/helpers'
import useDirectories from '@/utils/useDirectories'

import { createTransform } from '../ArticlePage/ArticlePage'
import Errors from '../ArticlePage/Errors'
import PluginError from '../PluginError'

import GuideNav from './GuideNav'
import styles from './GuidePage.module.scss'
import GuideSidebar from './GuideSidebar'

const DEFAULT_ICON = 'fa-solid fa-magnifying-glass'

export type TocType = Array<{
  header: string
  icon: string
  rows: Array<{
    heading: string
    key: string
    ref: React.RefObject<Element>
  }>
}>

const replaceContent = (content: string) => {
  const replaceCardTags = ({
    baseTag,
    className,
    content,
    tag,
  }: {
    baseTag: string
    className?: string
    content: string
    tag: string
  }) => {
    // Regex to match a wp shortcode
    const regex = new RegExp(`\\[${tag}(\\s+[^\\]]*)?\\](.*?)\\[\\/${tag}\\]`, 'gs')

    const cleanNames = (names: string) =>
      names
        .replace(/<\/?p>/g, '')
        .replace(/<br\s*\/?>/gi, '\n')
        .split('\n')
        .map((name) => name.trim())
        .filter(Boolean)

    // Attribute parsing helper function
    const parseAttrs = (attrString: string) =>
      attrString
        ?.split(' ')
        .filter((attr) => attr.includes('='))
        .reduce(
          (acc, attr) => {
            const [key, value] = attr.split('=')
            acc[key] = value.replace(/['"]/g, '')
            return acc
          },
          {} as Record<string, string>,
        )

    // Shortcode handlers
    // Replace the shortcode with the appropriate HTML
    if (baseTag === 'deck') {
      return content.replace(regex, (_, attrString: string, deckContent: string) => {
        const titleStart = attrString.indexOf('title=')
        const title = titleStart !== -1 ? attrString.slice(titleStart + 6) : ''
        const lines = cleanNames(deckContent)
        return (
          `<div class="edhrecp__deck mtgh"><span class="edhrecp__deck-s" name="${title}" site="edhrec" cards="` +
          lines.join('\n') +
          `"></span></div>`
        )
      })
    } else if (baseTag === 'ec') {
      return content.replace(regex, (_, _attrString: string, names: string) => {
        const nameDivs = cleanNames(names)
          .map((name) => `<div class="edhrecp__card-container" name="${name}"></div>`)
          .join('')
        return `<div class="edhrecp__cards${className ? ` ${className}` : ''}">${nameDivs}</div>`
      })
    } else if (baseTag === 'el') {
      return content.replace(regex, (_, attrString: string, name: string) => {
        const fixedName = name.trim()
        const attrs = parseAttrs(attrString)
        const renderedName = attrs?.value || fixedName.split('|')[0]
        const nameDiv =
          `<a class="edhrecp__link-a" content="${fixedName}" href="#" target="_blank">` +
          renderedName +
          `<span class="edhrecp__link-image"></span></a>`
        return `<span class="edhrecp__link${className ? ` ${className}` : ''}">${nameDiv}</span>`
      })
    } else if (baseTag === 'et') {
      return content.replace(regex, (_, _attrString: string, names: string) => {
        const cleanedNames = cleanNames(names)
        const nameDivs = cleanedNames.map((name) => `<tr><td>[el]${name}[/el]</td></tr>`).join('')
        return `<table class="edhrecp__table">${nameDivs}</table>`
      })
    } else if (baseTag === 'm') {
      return content.replace(regex, (_, _attrString: string, names: string) => {
        const manaDivs = cleanNames(names)
          .map(
            (name) =>
              `<div class="mana-container">` +
              `<i class="ms ms-cost ms-${name.toLowerCase()}" style="margin-left: inherit !important"></i>` +
              `</div>`,
          )
          .join('')
        return `<span class="mana-container-container">${manaDivs}</span>`
      })
    } else {
      return content
    }
  }

  const tagConfig = [
    { baseTag: 'et', tags: ['et', 'edhrec_table'] }, // Must come before [el]
    { baseTag: 'ec', tags: ['ec', 'edhrec_cards'] },
    { baseTag: 'ec', className: 'rotate', tags: ['ecn', 'edhrec_cards_ninety'] },
    { baseTag: 'ec', className: 'reverse', tags: ['ecr', 'edhrec_cards_reverse'] },
    { baseTag: 'ec', className: 'only-back', tags: ['eco', 'edhrec_cards_onlyback'] },
    { baseTag: 'ec', className: 'only-front', tags: ['ecf', 'edhrec_cards_onlyfront'] },
    { baseTag: 'ec', className: 'side-by-side', tags: ['ecs', 'edhrec_cards_sidebyside'] },
    { baseTag: 'el', tags: ['el', 'edhrec_link', 'card'] },
    { baseTag: 'el', className: 'rotate', tags: ['eln', 'edhrec_link_ninety'] },
    { baseTag: 'el', className: 'reverse', tags: ['elr', 'edhrec_link_reverse'] },
    { baseTag: 'el', className: 'only-back', tags: ['elo', 'edhrec_link_onlyback'] },
    { baseTag: 'el', className: 'only-front', tags: ['elf', 'edhrec_link_onlyfront'] },
    { baseTag: 'el', className: 'side-by-side', tags: ['els', 'edhrec_link_sidebyside'] },
    { baseTag: 'deck', tags: ['deck'] },
    { baseTag: 'm', tags: ['m', 'mana'] },
  ]

  tagConfig.forEach(({ baseTag, className, tags }) => {
    tags.forEach((tag) => {
      content = replaceCardTags({ baseTag, className, content, tag })
    })
  })

  return '<p>' + content.replaceAll('\r\n\r\n', '</p><p>') + '</p>'
}

type Props = { enableAdhesion: boolean; post: PostType }

const GuidePage = ({
  enableAdhesion,
  post: { author, content, excerpt, featuredImageUrl, guide, slug, tags, title },
}: Props) => {
  const { subpage } = useDirectories()
  const cardNamesRef = useRef<string[]>([])
  const errorsRef = useRef<string[]>([])
  const keyRef = useRef<number>(0)
  const [isClient, setIsClient] = useState(false)

  const isPreview = subpage === 'preview'
  const toc: TocType = []
  const transform = createTransform({
    cardNamesRef,
    errorsRef,
    isClient,
    isPreview,
    keyRef,
    slug,
    title,
  })

  useEffect(() => {
    if (!isClient) setIsClient(true)
  }, [])

  return (
    <Main artCrop={featuredImageUrl}>
      <ExplicitHead
        description={excerpt}
        imageUrl={featuredImageUrl}
        keywords={tags?.map((tag) => tag.name).join(', ')}
        title={title}
      />
      <link
        href='https://edhrec.com/articles/wp-content/plugins/edhrec-plugin/build/styles.css'
        id='MyPluginStyles-css'
        media='all'
        rel='stylesheet'
        type='text/css'
      />
      {/* eslint-disable-next-line @next/next/no-css-tags*/}
      <link
        href='//cdn.jsdelivr.net/npm/mana-font@latest/css/mana.min.css'
        id='mana-font'
        rel='stylesheet'
        type='text/css'
      />
      <link href='https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.0/css/all.min.css' rel='stylesheet' />
      <div
        // data-blocklist-adhesion-tablet='1'
        // data-blocklist-auto-insert-sticky='1'
        // data-blocklist-chicory='1'
        // data-blocklist-content-desktop='1'
        // data-blocklist-content-mobile='1'
        // data-blocklist-in-image='1'
        // data-blocklist-interstitial-desktop='1'
        // data-blocklist-interstitial-mobile='1'
        // data-blocklist-leaderboard='1'
        // data-blocklist-recipe='1'
        // data-blocklist-sidebar-atf='1'
        // data-blocklist-sidebar-btf='1'
        // data-blocklist-zergnet='1'
        id='mediavine-settings'
      />
      <Container className='d-block d-md-flex flex-row' guide>
        <GuideSidebar author={author} title={title} toc={toc} />
        <div className='d-flex flex-column gap-3'>
          {isPreview && <Errors cardNames={cardNamesRef.current} errors={errorsRef.current} />}
          <BSCard>
            {/* <BSCard.Header className={classNames(styles.pageHeading, 'd-none d-md-flex shadow-sm')}>
              {title}
            </BSCard.Header> */}
            <div>
              <a href={featuredImageUrl} rel='noreferrer' target='_blank'>
                <img alt='' className='rounded-top shadow-sm w-100' src={featuredImageUrl} />
              </a>
              <div className='d-grid gap-3'>
                {cardNamesRef?.current.map((cardName, i) => (
                  <PluginError cardName={cardName} key={`plugin-error-${i}`} />
                ))}
              </div>
            </div>
            <div className={classNames('wp-content', styles.content)}>{ReactHtmlParser(content, { transform })}</div>
            <GuideNav toc={toc} />
          </BSCard>
          <div className='d-flex flex-column gap-1'>
            {guide && // Fix for empty guides
              guide
                .filter(({ sections }) => sections)
                .flatMap(({ chapter, icon, sections }) => {
                  toc.push({
                    header: chapter,
                    icon: `fa-solid fa-${icon}` || DEFAULT_ICON,
                    rows: [],
                  })

                  const out = sections.map(({ section, section_content }, i) => {
                    const replacedSectionContent = replaceContent(section_content)
                    const tocRow = {
                      heading: section,
                      key: v4(),
                      ref: createRef<HTMLDivElement>(),
                    }
                    toc[toc.length - 1].rows.push(tocRow)

                    return (
                      <BSCard className='shadow-sm mb-3' key={tocRow.key} ref={tocRow.ref}>
                        <a id={formatUrl(`${chapter}-${section}`)} />
                        <BSCard.Header className={classNames(styles.sectionHeading, 'shadow-sm')}>
                          <span>
                            {i + 1}. {section}
                          </span>
                        </BSCard.Header>
                        <BSCard.Text className={classNames('wp-content', styles.content)}>
                          {ReactHtmlParser(replacedSectionContent, { transform })}
                        </BSCard.Text>
                      </BSCard>
                    )
                  })
                  return (
                    <Fragment key={v4()}>
                      <div className={styles.contentHint}>
                        <div className='mv_slot_target' data-slot='content' />
                      </div>
                      <hr className='mb-0' />
                      <a id={formatUrl(`${chapter}`)} />
                      <h1 className={styles.chapterHeading}>
                        <i className={toc[toc.length - 1].icon} /> {chapter}
                      </h1>
                      {out}
                    </Fragment>
                  )
                })}
          </div>
        </div>
        {enableAdhesion && <div className='mvHomeAdhesion' />}
      </Container>
    </Main>
  )
}

export default GuidePage
